import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    CreditCard,
    ArrowLeft,
    CheckCircle,
    Smartphone,
    Building,
    QrCode,
    ChevronRight,
    Wallet,
    Banknote,
    X,
    Lock,
    Shield,
    Receipt,
    AlertCircle,
    RefreshCw,
    Tag,
    User,
    ShoppingBag,
    Printer,
    Check,
    ChevronLeft,
    Truck
} from 'lucide-react';
import { NotificationContext } from '../context/NotificationContext';
import { salesAPI, productAPI, storeAPI } from '../api/apiService';
import { Gift } from 'lucide-react';
import { resolveMediaUrl } from "../utils/resolveMediaUrl";

export default function POSPaymentPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const { addNotification } = useContext(NotificationContext);

    // Bill Details
    const billDetails = location.state || {};
    const [items, setItems] = useState(billDetails.items || []);
    const totalAmount = billDetails.total || 0;
    const invoiceNo = billDetails.invoiceNo || "INV-0000";
    const customer = billDetails.customer;
    const gstEnabled = billDetails.gstEnabled ?? true;
    const taxMode = billDetails.taxMode || "exclusive";
    const taxRate = billDetails.taxRate ?? 18;

    // State
    const [step, setStep] = useState(1); // 1: Payment, 3: Success, 4: Failure
    const [sidebarStep, setSidebarStep] = useState(1); // 1: Selection, 2: Confirmation
    const [selectedMethod, setSelectedMethod] = useState(null); 
    const [processing, setProcessing] = useState(false);
    const [products, setProducts] = useState([]);
    const [selectedGiftId, setSelectedGiftId] = useState('');

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const res = await productAPI.getAllProducts({ limit: 500 });
                if (res.data && res.data.results) {
                    setProducts(res.data.results);
                } else if (Array.isArray(res.data)) {
                    setProducts(res.data);
                }
            } catch (err) {
                console.error("Failed to load products for gift", err);
            }
        };
        fetchProducts();
    }, []);

    const [upiDetails, setUpiDetails] = useState(null);

    useEffect(() => {
        const fetchStoreSettings = async () => {
            try {
                const response = await storeAPI.getAll();
                let store = null;
                if (response.data && response.data.length > 0) {
                    store = response.data[0];
                } else if (response.data && response.data.results && response.data.results.length > 0) {
                    store = response.data.results[0];
                }
                
                if (store && store.upi_id) {
                    setUpiDetails({
                        upi_id: store.upi_id,
                        upi_qr_image_url: store.upi_qr_image_url,
                        account_holder_name: store.account_holder_name,
                        bank_name: store.bank_name
                    });
                }
            } catch (error) {
                console.error("Failed to load store settings", error);
            }
        };
        fetchStoreSettings();
    }, []);

    const handleAddGift = () => {
        if (!selectedGiftId) return;
        const giftProd = products.find(p => p.id.toString() === selectedGiftId);
        if (!giftProd) return;
        
        setItems(prev => [...prev, {
            id: giftProd.id,
            name: giftProd.name,
            sku: giftProd.product_code,
            price: 0,
            qty: 1,
            tax_rate: 0,
            discount: 0,
            is_gift: true,
            asn_code: giftProd.asn_code || ''
        }]);
        setSelectedGiftId('');
        addNotification("Gift Product Added", "success");
    };

    // Input States
    const [amountReceived, setAmountReceived] = useState(''); // For Cash
    const [cardRefNo, setCardRefNo] = useState(''); // For External/UPI Ref
    const [splitCash, setSplitCash] = useState('');
    const [splitUpi, setSplitUpi] = useState('');

    // Calculations (Sync with POSBillingPage)
    const rawSubtotal = useMemo(() => items.reduce((sum, i) => sum + (i.price * i.qty), 0), [items]);
    const totalSavings = useMemo(() => items.reduce((sum, i) => sum + (parseFloat(i.discount) || 0), 0), [items]);
    const subtotal = rawSubtotal - totalSavings;
    
    const { tax, finalTotal, displayTaxRate } = useMemo(() => {
        let currentTax = 0;
        let currentTotal = subtotal;
        let rateForDisplay = taxRate;

        if (gstEnabled) {
            currentTax = items.reduce((acc, item) => {
                const itemTaxRate = parseFloat(item.tax_rate) ?? taxRate;
                const itemSubtotal = (item.price * item.qty) - (item.discount || 0);
                
                if (taxMode === 'inclusive') {
                    return acc + (itemSubtotal - (itemSubtotal / (1 + itemTaxRate / 100)));
                } else {
                    return acc + (itemSubtotal * (itemTaxRate / 100));
                }
            }, 0);

            if (taxMode === 'exclusive') {
                currentTotal = subtotal + currentTax;
            } else {
                currentTotal = subtotal;
            }

            const uniqueRates = [...new Set(items.map(item => parseFloat(item.tax_rate) ?? taxRate))];
            rateForDisplay = uniqueRates.length === 1 ? uniqueRates[0] : (uniqueRates.length > 0 ? "Mixed" : taxRate);
        }
        return { tax: currentTax, finalTotal: currentTotal, displayTaxRate: rateForDisplay };
    }, [subtotal, items, gstEnabled, taxMode, taxRate]);

    // Derived State for Success Screen
    const [successDetails, setSuccessDetails] = useState(null);

    // Handlers
    const handleMethodSelect = (methodId) => {
        setSelectedMethod(methodId);
        setSidebarStep(2);
        // Reset specific inputs when switching
        setAmountReceived('');
        setCardRefNo('');
        setSplitCash('');
        setSplitUpi('');
    };

    // Auto-fill split balance
    useEffect(() => {
        if (selectedMethod === 'split') {
            const cash = parseFloat(splitCash) || 0;
            if (cash <= finalTotal) {
                setSplitUpi((finalTotal - cash).toFixed(2));
            } else {
                setSplitUpi('0');
            }
        }
    }, [splitCash, finalTotal, selectedMethod]);

    const processPayment = async () => {
        // Validation
        if (items.length === 0) {
            addNotification("Cart is empty. Please add items before proceeding with payment.", "error");
            return;
        }
        
        if (selectedMethod === 'cash') {
            const received = parseFloat(amountReceived) || 0;
            if (received < finalTotal) {
                addNotification(`Amount must be at least ₹${finalTotal.toFixed(0)}`, "error");
                return;
            }
        }
        if (selectedMethod === 'split') {
            const cash = parseFloat(splitCash) || 0;
            const upi = parseFloat(splitUpi) || 0;
            if (Math.abs((cash + upi) - finalTotal) > 1) {
                addNotification(`Total must equal ₹${finalTotal.toFixed(0)}`, "error");
                return;
            }
        }

        setProcessing(true);

        try {
            const isGstBill = billDetails.billingType === "GST Bill";
            const invoiceData = {
                customer: customer?.id || null,
                customer_id: customer?.id || null,
                status: "completed",
                payment_status: selectedMethod === 'cod' ? "unpaid" : "paid",
                billing_mode: gstEnabled ? 'with_gst' : 'without_gst',
                tax_mode: taxMode,
                payment_mode: selectedMethod === 'split' ? 'Split' : 
                              selectedMethod === 'cod' ? 'COD' :
                              (selectedMethod.charAt(0).toUpperCase() + selectedMethod.slice(1)),
                tax_rate: typeof displayTaxRate === 'number' ? displayTaxRate : 0,
                subtotal: parseFloat(rawSubtotal).toFixed(2),
                discount_amount: parseFloat(totalSavings).toFixed(2),
                discount_percent: 0,
                items: items.map(i => ({
                    id: i.id,
                    variant_id: i.variant_id || null,
                    qty: i.qty,
                    price: parseFloat(i.price).toFixed(2),
                    name: i.name,
                    discount: parseFloat(i.discount || 0).toFixed(2),
                    tax: parseFloat(i.tax_rate || 0).toFixed(2),
                    is_gift: i.is_gift || false
                })),
                paid_amount: selectedMethod === 'cod' ? 0 : parseFloat(finalTotal).toFixed(2)
            };

            const response = await salesAPI.createSale(invoiceData);
            setProcessing(false);

            const received = parseFloat(amountReceived) || 0;
            const formattedMethod = selectedMethod === 'upi' ? 'UPI / Online' :
                selectedMethod === 'cod' ? 'Cash on Delivery' :
                selectedMethod.charAt(0).toUpperCase() + selectedMethod.slice(1);

            navigate('/pos/invoice-success', {
                state: {
                    ...response.data,
                    totalAmount: response.data.total_amount,
                    subtotal: response.data.subtotal,
                    invoiceNo: response.data.invoice_number,
                    cgst: parseFloat(response.data.cgst_amount) || 0,
                    sgst: parseFloat(response.data.sgst_amount) || 0,
                    igst: parseFloat(response.data.igst_amount) || 0,
                    taxAmount: (parseFloat(response.data.cgst_amount) || 0) + (parseFloat(response.data.sgst_amount) || 0) + (parseFloat(response.data.igst_amount) || 0),
                    taxRate: response.data.tax_rate,
                    gst_summary: response.data.gst_summary,
                    items: items,
                    customer: customer,
                    billingType: billDetails.billingType || "Standard",
                    billingMode: response.data.billing_mode,
                    billerName: response.data.created_by_name,
                    billerId: response.data.created_by_salesman_id,
                    successDetails: {
                        method: selectedMethod,
                        paymentMethod: formattedMethod,
                        cardRefNo: (selectedMethod === 'card' || selectedMethod === 'upi' || selectedMethod === 'split') ? cardRefNo : null,
                        cashHandover: selectedMethod === 'cash' ? received : (selectedMethod === 'split' ? parseFloat(splitCash) : finalTotal),
                        changeDue: selectedMethod === 'cash' ? received - finalTotal : 0,
                        splitDetails: selectedMethod === 'split' ? { cash: parseFloat(splitCash) || 0, upi: parseFloat(splitUpi) || 0 } : null,
                        paidAmount: response.data.paid_amount
                    }
                }
            });
            addNotification("Payment Successful!", "success");

        } catch (error) {
            console.error("Payment Error:", error);
            setProcessing(false);
            const errorMsg = error.response?.data?.detail || error.response?.data?.message || "Payment Failed. Please try again.";
            addNotification(errorMsg, "error");
        }
    };

    // --- Components ---

    const PaymentCard = ({ id, icon: Icon, title, description, active, onClick }) => (
        <div
            onClick={() => onClick(id)}
            className={`
                p-5 rounded-3xl border-2 cursor-pointer transition-all duration-300 flex flex-col items-center text-center gap-2 relative group
                ${active 
                    ? 'border-green-500 bg-green-50 shadow-xl shadow-green-100' 
                    : 'border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50 shadow-sm'}
            `}
        >
            <div className={`
                w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-300 group-hover:scale-110
                ${active ? 'bg-green-600 text-white shadow-lg shadow-green-200' : 'bg-gray-50 text-gray-400 group-hover:bg-white'}
            `}>
                <Icon className="w-6 h-6" />
            </div>
            <div>
                <h4 className={`font-black text-[12px] uppercase tracking-wider ${active ? 'text-green-800' : 'text-gray-900'}`}>{title}</h4>
                <p className={`text-[10px] font-bold mt-1 ${active ? 'text-green-600' : 'text-gray-400'}`}>{description}</p>
            </div>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                <ChevronRight className="w-4 h-4 text-gray-300" />
            </div>
        </div>
    );

    if (items.length === 0) return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-10 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-3xl flex items-center justify-center mb-6 opacity-40">
                <ShoppingBag className="w-10 h-10 text-gray-400" />
            </div>
            <h2 className="text-2xl font-black text-gray-900 mb-2">No Bill Found</h2>
            <p className="text-gray-500 mb-8 max-w-xs mx-auto font-medium">It looks like your cart is empty or the session expired. Please go back and add items.</p>
            <button 
                onClick={() => navigate('/pos')}
                className="px-8 py-4 bg-green-600 text-white rounded-2xl font-black uppercase tracking-widest shadow-xl shadow-green-100 hover:bg-green-700 transition-all active:scale-95"
            >
                Back to POS
            </button>
        </div>
    );

    const selectedMethodInfo = {
        cash: { title: "Pay on cash", icon: Banknote, color: "green" },
        card: { title: "Pay using card", icon: CreditCard, color: "blue" },
        upi: { title: "Pay using UPI / Scan", icon: QrCode, color: "blue" },
        split: { title: "Split payment", icon: Wallet, color: "purple" },
        cod: { title: "Cash on Delivery", icon: Truck, color: "orange" }
    }[selectedMethod] || {};

    return (
        <div className="min-h-screen bg-green-50/30 flex flex-col font-sans text-gray-900 overflow-hidden">
            
            {/* Top Navigation */}
            <header className="h-20 bg-white/80 backdrop-blur-md border-b border-green-100/50 flex items-center justify-between px-10 z-50">
                <div className="flex items-center gap-10">
                    <button 
                        onClick={() => navigate('/pos')}
                        className="flex items-center gap-3 px-5 py-2.5 bg-white border border-gray-100 rounded-xl font-black text-xs uppercase tracking-widest text-gray-400 hover:text-gray-900 hover:border-gray-900 transition-all group"
                    >
                        <ChevronLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" /> Back to menu
                    </button>
                    
                    {/* Customer Badge */}
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-green-100 overflow-hidden">
                            {customer?.image ? <img src={customer.image} alt="" className="w-full h-full object-cover" /> : <User className="w-5 h-5" />}
                        </div>
                        <div>
                            {customer?.name ? (
                                <>
                                    <h3 className="font-black text-gray-900 flex items-center gap-2 leading-none text-sm">
                                        {customer.name}
                                        <span className="bg-green-50 text-green-600 text-[9px] uppercase font-black px-2 py-0.5 rounded-full border border-green-100 tracking-wider">Regular</span>
                                    </h3>
                                    <p className="text-[10px] font-bold text-gray-400 mt-1 flex items-center gap-1">
                                        <Smartphone className="w-2.5 h-2.5" /> {customer.phone}
                                    </p>
                                </>
                            ) : (
                                <div className="flex flex-col gap-1">
                                    <span className="bg-green-50 text-green-600 text-[9px] uppercase font-black px-3 py-1 rounded-full border border-green-100 tracking-[0.1em]">Walk-in Customer</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    <div className="p-2.5 bg-gray-50 text-gray-400 rounded-xl border border-gray-100">
                        <Lock className="w-4 h-4" />
                    </div>
                </div>
            </header>

            <main className="flex-1 flex overflow-hidden">
                
                {/* LEFT COLUMN: Order Details */}
                <section className="flex-1 overflow-y-auto p-8 space-y-8 scrollbar-hide bg-green-50/20">
                    
                    {/* Items Card */}
                    <div className="bg-white rounded-[40px] border border-gray-100 shadow-sm overflow-hidden">
                        <div className="px-10 py-8 border-b border-gray-50 flex items-center justify-between">
                            <h3 className="text-xl font-black text-gray-900 flex items-center gap-4">
                                <div className="w-10 h-10 bg-green-50 rounded-2xl flex items-center justify-center text-green-600">
                                    <ShoppingBag className="w-5 h-5" />
                                </div>
                                Current Order Records
                            </h3>
                            <div className="flex items-center gap-3">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] bg-gray-50 px-3 py-1 rounded-full">Inv No: {invoiceNo}</span>
                            </div>
                        </div>
                        
                        <div className="p-4">
                            <table className="w-full text-left">
                                <thead className="text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">
                                    <tr>
                                        <th className="px-6 py-6 font-bold">Dish name</th>
                                        <th className="px-6 py-6 font-bold">Add ons / SKU</th>
                                        <th className="px-6 py-6 font-bold">Quantity</th>
                                        <th className="px-6 py-6 font-bold text-right">Amounts</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {items.map((item, idx) => (
                                        <tr key={idx} className="group hover:bg-gray-50/50 transition-colors">
                                            <td className="px-6 py-6">
                                                <div className="flex items-center gap-5">
                                                    <div className="w-14 h-14 bg-gray-100 rounded-2xl overflow-hidden flex-shrink-0 border border-gray-200">
                                                        {item.image ? (
                                                            <img 
                                                                src={resolveMediaUrl(item.image)}
                                                                className="w-full h-full object-cover" 
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center text-2xl">📦</div>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <p className="font-black text-base text-gray-900">
                                                            {item.name}
                                                            {item.is_gift && (
                                                                <span className="ml-2 bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-[10px] uppercase font-black">
                                                                    Gift Product
                                                                </span>
                                                            )}
                                                        </p>
                                                        {item.variant_name && <p className="text-[11px] font-bold text-green-500 mt-1 uppercase tracking-wider">{item.variant_name}</p>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-6">
                                                <div className="bg-green-50 text-green-700 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider inline-flex items-center gap-2 border border-green-100/50">
                                                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.5)]"></div>
                                                    Standard Packaging
                                                </div>
                                            </td>
                                            <td className="px-6 py-6">
                                                <span className="text-sm font-black text-gray-300">x</span>
                                                <span className="text-lg font-black text-gray-900 ml-2 tracking-tight">{parseFloat(item.qty)} {item.unit || 'pc'}</span>
                                            </td>
                                            <td className="px-6 py-6 text-right font-black text-lg text-gray-900 tracking-tight">
                                                ₹{((item.price * item.qty) - (item.discount || 0)).toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                        {/* Coupon & Gift Section */}
                        <div className="flex flex-col gap-6">
                            <div className="bg-white rounded-[32px] border border-gray-100 shadow-sm p-8 flex flex-col justify-between">
                                <div>
                                    <div className="flex items-center gap-4 mb-4">
                                        <div className="w-12 h-12 bg-green-50 text-green-500 rounded-[16px] flex items-center justify-center border border-green-100">
                                            <Tag className="w-5 h-5" />
                                        </div>
                                        <h4 className="text-lg font-black text-gray-900">Discount coupon</h4>
                                    </div>
                                    <p className="text-[10px] font-bold text-gray-400 mb-6 leading-relaxed">
                                        Apply valid coupons for special discounts on this total.
                                    </p>
                                </div>
                                <div className="flex gap-4 p-1.5 bg-gray-50 rounded-xl border border-gray-100">
                                    <input 
                                        type="text" 
                                        placeholder="Coupon code" 
                                        className="flex-1 bg-transparent px-4 py-3 text-[10px] font-black focus:outline-none placeholder:text-gray-300"
                                    />
                                    <button className="px-6 py-3 bg-green-500 text-white rounded-lg font-black text-[10px] uppercase tracking-widest hover:bg-green-600 shadow-lg shadow-green-100 transition-all hover:scale-[1.02] active:scale-95">Apply</button>
                                </div>
                            </div>
                            
                            {/* Gift Product Section */}
                            <div className="bg-white rounded-[32px] border border-gray-100 shadow-sm p-8 flex flex-col justify-between">
                                <div>
                                    <div className="flex items-center gap-4 mb-4">
                                        <div className="w-12 h-12 bg-purple-50 text-purple-500 rounded-[16px] flex items-center justify-center border border-purple-100">
                                            <Gift className="w-5 h-5" />
                                        </div>
                                        <h4 className="text-lg font-black text-gray-900">Gift Product</h4>
                                    </div>
                                    <p className="text-[10px] font-bold text-gray-400 mb-6 leading-relaxed">
                                        Select a product to gift to the customer for free.
                                    </p>
                                </div>
                                <div className="flex gap-4 p-1.5 bg-gray-50 rounded-xl border border-gray-100">
                                    <select
                                        value={selectedGiftId}
                                        onChange={(e) => setSelectedGiftId(e.target.value)}
                                        className="flex-1 bg-transparent px-4 py-3 text-[10px] font-black focus:outline-none text-gray-700 w-full truncate"
                                    >
                                        <option value="">Select a product...</option>
                                        {products.map(p => (
                                            <option key={p.id} value={p.id}>{p.name} ({p.product_code})</option>
                                        ))}
                                    </select>
                                    <button onClick={handleAddGift} disabled={!selectedGiftId} className="px-6 py-3 bg-purple-500 text-white rounded-lg font-black text-[10px] uppercase tracking-widest hover:bg-purple-600 shadow-lg shadow-purple-100 transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed">Add Gift</button>
                                </div>
                            </div>
                        </div>

                        {/* Summary Section */}
                        <div className="bg-white rounded-[32px] border border-gray-100 shadow-sm p-8 space-y-4">
                            <div className="flex justify-between items-center text-[10px] font-bold">
                                <span className="text-gray-400 uppercase tracking-[0.2em] font-black">Subtotal</span>
                                <span className="text-gray-900 font-black text-sm">₹{rawSubtotal.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between items-center text-[10px] font-bold">
                                <span className="text-gray-400 uppercase tracking-[0.2em] font-black">
                                    CGST {taxMode === 'inclusive' && <span className="text-blue-500 font-bold ml-1 lowercase">(Incl.)</span>}
                                </span>
                                <div className="flex items-center gap-3 text-green-600 bg-green-50 px-2 py-0.5 rounded-lg border border-green-100">
                                    <span className="font-black text-xs">₹{(tax / 2).toFixed(2)}</span>
                                </div>
                            </div>
                            <div className="flex justify-between items-center text-[10px] font-bold">
                                <span className="text-gray-400 uppercase tracking-[0.2em] font-black">
                                    SGST {taxMode === 'inclusive' && <span className="text-blue-500 font-bold ml-1 lowercase">(Incl.)</span>}
                                </span>
                                <div className="flex items-center gap-3 text-green-600 bg-green-50 px-2 py-0.5 rounded-lg border border-green-100">
                                    <span className="font-black text-xs">₹{(tax / 2).toFixed(2)}</span>
                                </div>
                            </div>
                            <div className="flex justify-between items-center text-[10px] font-bold">
                                <span className="text-gray-400 uppercase tracking-[0.2em] font-black">Special discount</span>
                                <div className="flex items-center gap-3 text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-lg border border-emerald-100">
                                    <span className="text-[9px] font-black tracking-tight">{totalSavings > 0 ? 'SAVED' : 'None'}</span>
                                    <span className="font-black text-sm">- ₹{totalSavings.toFixed(0)}</span>
                                </div>
                            </div>
                            <div className="h-px bg-gray-100/60 my-4"></div>
                            <div className="flex justify-between items-end">
                                <div>
                                    <span className="block text-[8px] font-black text-gray-400 uppercase tracking-[0.3em] mb-1">Total payable</span>
                                    <span className="text-base font-black text-gray-900 uppercase">Grand Net Total</span>
                                </div>
                                <span className="text-4xl font-black text-green-600 tracking-tighter leading-none">
                                    <span className="text-xl mr-1 opacity-40 font-black">₹</span>
                                    {finalTotal.toLocaleString()}
                                </span>
                            </div>
                        </div>
                    </div>
                </section>

                {/* RIGHT COLUMN: Payment Selection */}
                <section className="w-[380px] bg-white border-l border-gray-200 p-8 flex flex-col shadow-[0_0_80px_rgba(0,0,0,0.05)] z-40">
                    
                    <AnimatePresence mode="wait">
                        {sidebarStep === 1 ? (
                            <motion.div 
                                key="selection"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="flex-1 flex flex-col"
                            >
                                <div className="text-center mb-8">
                                    <h2 className="text-2xl font-black text-gray-900 tracking-tight">Select payment mode</h2>
                                    <p className="text-[10px] font-bold text-gray-400 mt-2 max-w-[240px] mx-auto leading-relaxed uppercase tracking-widest">
                                        Choose method to proceed with final confirmation
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 gap-3 overflow-y-auto pr-1 scrollbar-hide py-1">
                                    {[
                                        {
                                            id: "card",
                                            icon: CreditCard,
                                            title: "Pay using card",
                                            description: "Complete payment via credit/debit swipe",
                                            hidden: true, // Card payment is hidden from UI (not removed)
                                        },
                                        {
                                            id: "cash",
                                            icon: Banknote,
                                            title: "Pay on cash",
                                            description: "Complete order payment using physical cash",
                                            hidden: false,
                                        },
                                        {
                                            id: "upi",
                                            icon: QrCode,
                                            title: "Pay using UPI / Scan",
                                            description: "Ask customer to pay or scan QR externally",
                                            hidden: false,
                                        },
                                        {
                                            id: "split",
                                            icon: Wallet,
                                            title: "Split payment",
                                            description: "Record cash + online mixed payment",
                                            hidden: false,
                                        },
                                        {
                                            id: "cod",
                                            icon: Truck,
                                            title: "Cash on Delivery",
                                            description: "Payment will be collected at delivery",
                                            hidden: false,
                                        },
                                    ]
                                        .filter((m) => !m.hidden)
                                        .map((m) => (
                                            <PaymentCard
                                                key={m.id}
                                                id={m.id}
                                                icon={m.icon}
                                                title={m.title}
                                                description={m.description}
                                                active={selectedMethod === m.id}
                                                onClick={handleMethodSelect}
                                            />
                                        ))}
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div 
                                key="confirmation"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="flex-1 flex flex-col justify-between"
                            >
                                <div className="space-y-6">
                                    <div className="flex items-center gap-4">
                                        <button 
                                            onClick={() => setSidebarStep(1)}
                                            className="w-10 h-10 bg-gray-50 text-gray-400 rounded-xl flex items-center justify-center border border-gray-100 hover:bg-white hover:text-gray-900 transition-all hover:scale-105"
                                        >
                                            <ChevronLeft className="w-5 h-5" />
                                        </button>
                                        <div>
                                            <h2 className="text-xl font-black text-gray-900">Confirmation</h2>
                                            <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest leading-none">Enter details to finish</p>
                                        </div>
                                    </div>

                                    {/* Selected Method Header Display */}
                                    <div className="p-5 bg-green-50/50 rounded-[32px] border border-green-100/50 flex flex-col items-center text-center gap-2 relative overflow-hidden group">
                                        <div className="absolute -right-4 -top-4 w-20 h-20 bg-green-100/20 rounded-full blur-2xl"></div>
                                        <div className={`w-14 h-14 bg-green-600 text-white rounded-2xl flex items-center justify-center shadow-lg shadow-green-200`}>
                                            {selectedMethodInfo.icon && <selectedMethodInfo.icon className="w-7 h-7" />}
                                        </div>
                                        <div>
                                            <h4 className="font-black text-base text-gray-900">{selectedMethodInfo.title}</h4>
                                            <span className="text-[10px] font-black text-green-600 uppercase tracking-[0.2em]">Ready for billing</span>
                                        </div>
                                    </div>

                                    {/* Method Specific Inputs */}
                                    <div className="p-0.5 w-full">
                                        {selectedMethod === 'cash' && (
                                            <div className="space-y-4">
                                                <div className="flex justify-between items-center px-1">
                                                    <label className="text-[9px] font-black text-gray-400 uppercase tracking-[0.2em]">Amount Received</label>
                                                    {parseFloat(amountReceived) > finalTotal && (
                                                        <span className="text-[9px] font-black text-green-600 bg-green-50 px-2 py-0.5 rounded-full border border-green-100 flex items-center gap-1">
                                                            Return ₹{(amountReceived - finalTotal).toFixed(0)}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="relative group">
                                                    <div className="absolute inset-0 bg-green-400 rounded-3xl blur opacity-0 group-focus-within:opacity-10 transition-opacity"></div>
                                                    <input 
                                                        type="number" 
                                                        placeholder={`₹${finalTotal.toFixed(0)}`}
                                                        value={amountReceived}
                                                        onChange={(e) => setAmountReceived(e.target.value)}
                                                        className="w-full bg-gray-50 border-2 border-transparent p-5 rounded-2xl text-2xl font-black focus:bg-white focus:border-green-500 outline-none transition-all placeholder:text-gray-200 relative z-10"
                                                        autoFocus
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {(selectedMethod === 'card' || selectedMethod === 'upi') && (
                                            <div className="space-y-4">
                                                {selectedMethod === 'upi' && upiDetails && (
                                                    <div className="bg-white p-4 rounded-2xl border border-gray-100 flex flex-col items-center mb-4 shadow-sm">
                                                        <h4 className="text-sm font-black text-gray-900 mb-2 uppercase tracking-wider">Scan to Pay</h4>
                                                        {upiDetails.upi_qr_image_url ? (
                                                            <div className="w-48 h-48 bg-white rounded-xl overflow-hidden mb-3 border-2 border-green-100 p-2 shadow-inner">
                                                                <img src={upiDetails.upi_qr_image_url.startsWith('http') ? upiDetails.upi_qr_image_url : resolveMediaUrl(upiDetails.upi_qr_image_url)} alt="UPI QR" className="w-full h-full object-contain" />
                                                            </div>
                                                        ) : (
                                                            <div className="w-40 h-40 bg-gray-50 rounded-xl flex items-center justify-center mb-3 border-2 border-dashed border-gray-200">
                                                                <QrCode className="w-10 h-10 text-gray-300" />
                                                            </div>
                                                        )}
                                                        <div className="text-center">
                                                            <p className="text-sm font-black text-gray-900 uppercase">{upiDetails.account_holder_name || "Merchant Account"}</p>
                                                            <p className="text-xs font-bold text-gray-500 mt-1 font-mono bg-gray-50 px-3 py-1 rounded-full inline-block border border-gray-100">{upiDetails.upi_id}</p>
                                                            {upiDetails.bank_name && <p className="text-[10px] font-bold text-gray-400 mt-1 uppercase">{upiDetails.bank_name}</p>}
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {selectedMethod === 'upi' && !upiDetails && (
                                                    <div className="bg-orange-50 p-6 rounded-2xl border border-orange-100 flex flex-col items-center text-center mb-4">
                                                        <AlertCircle className="w-8 h-8 text-orange-500 mb-3" />
                                                        <h4 className="text-sm font-black text-orange-900 uppercase">UPI Not Configured</h4>
                                                        <p className="text-xs font-bold text-orange-700 mt-2 max-w-[200px]">Please ask the store owner to upload a UPI QR code from Dashboard Settings.</p>
                                                    </div>
                                                )}

                                                <label className="text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] block px-1">Reference / UTR Number (Optional)</label>
                                                <input 
                                                    type="text" 
                                                    placeholder="Enter transaction ref / code"
                                                    value={cardRefNo}
                                                    onChange={(e) => setCardRefNo(e.target.value)}
                                                    className="w-full bg-gray-50 border-2 border-transparent p-5 rounded-2xl text-lg font-bold font-mono focus:bg-white focus:border-green-500 outline-none transition-all placeholder:text-gray-200"
                                                />
                                                <div className="text-[9px] font-bold text-gray-400 opacity-60 leading-relaxed italic uppercase tracking-wider text-center pt-2">
                                                    Verify payment on external terminal / app before confirming
                                                </div>
                                            </div>
                                        )}

                                        {selectedMethod === 'split' && (
                                            <div className="space-y-5">
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div className="space-y-2">
                                                        <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest block font-bold">Cash Part</label>
                                                        <input type="number" value={splitCash} onChange={(e) => setSplitCash(e.target.value)} className="w-full bg-gray-50 p-4 rounded-xl border-2 border-transparent focus:border-green-500 focus:bg-white outline-none font-black text-base transition-all" placeholder="0" />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <label className="text-[9px] font-black text-gray-500 uppercase tracking-widest block font-bold">UPI Part</label>
                                                        <input type="number" value={splitUpi} readOnly className="w-full bg-green-50 p-4 rounded-xl border border-green-100 font-black text-base text-green-600 outline-none shadow-inner" />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {selectedMethod === 'cod' && (
                                            <div className="space-y-4 p-5 bg-orange-50 rounded-2xl border border-orange-100 border-dashed">
                                                <div className="flex items-center gap-3 text-orange-600">
                                                    <Truck className="w-5 h-5" />
                                                    <span className="text-[10px] font-black uppercase tracking-widest">Delivery Collection</span>
                                                </div>
                                                <p className="text-[10px] font-bold text-orange-800/70 leading-relaxed uppercase tracking-tight">
                                                    The order will be marked as "Unpaid". Payment must be collected manually from the customer at the time of delivery.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="space-y-4 pt-4">
                                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <Shield className="w-3.5 h-3.5 text-emerald-500" />
                                            <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Secure Terminal</span>
                                        </div>
                                        <span className="text-xs font-black text-gray-900">₹{finalTotal.toLocaleString()}</span>
                                    </div>
                                    <button 
                                        onClick={processPayment}
                                        disabled={processing}
                                        className="w-full py-5 bg-[#00e676] hover:bg-[#00c853] text-gray-900 rounded-[28px] font-black text-lg uppercase tracking-widest shadow-[0_15px_30px_rgba(0,186,104,0.15)] transition-all transform active:scale-95 flex items-center justify-center gap-3 hover:scale-[1.02]"
                                    >
                                        {processing ? <RefreshCw className="w-6 h-6 animate-spin" /> : <CheckCircle className="w-6 h-6" />}
                                        {processing ? 'Processing...' : 'Confirm payment'}
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </section>
            </main>
        </div>
    );
}
